// src/redux/productsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Configuration from "../configuration";

export const fetchProducts = createAsyncThunk("products/fetchAll", async () => {
  const token = localStorage.getItem("x-access-token");
  const response = await fetch(Configuration.BACK_BASEURL + "products", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      /* "x-access-token": token, */
    },
  });
  const products = await response.json();
  return products;
});

export const productGetById = createAsyncThunk(
  "products/getById",
  async (id) => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(
      Configuration.BACK_BASEURL + `products/${id}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
      },
    );
    const product = await response.json();
    return product;
  },
);

export const productGetByReference = createAsyncThunk(
  "products/getByReference",
  async (reference) => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(
      Configuration.BACK_BASEURL + `products/reference/${reference}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
      },
    );
    const product = await response.json();
    return product;
  },
);

export const productAdded = createAsyncThunk(
  "products/create",
  async (productData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("x-access-token");

      // Don't set Content-Type header for FormData - browser will set it with boundary
      const response = await fetch(Configuration.BACK_BASEURL + "products", {
        method: "POST",
        headers: {
          Accept: "application/json",
          /* "x-access-token": token, */
          // Remove Content-Type - let browser set it with boundary
        },
        body: productData, // This is FormData, don't stringify
      });

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const product = await response.json();
      return product;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const productUpdated = createAsyncThunk(
  "products/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("x-access-token");

      const response = await fetch(
        Configuration.BACK_BASEURL + `products/${id}`,
        {
          method: "PUT",
          headers: {
            Accept: "application/json",
            /* "x-access-token": token, */
            // Remove Content-Type - let browser set it with boundary
          },
          body: data, // This is FormData, don't stringify
        },
      );

      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }

      const product = await response.json();
      return product;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const productDeleted = createAsyncThunk(
  "products/delete",
  async (id) => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(
      Configuration.BACK_BASEURL + `products/${id}`,
      {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
      },
    );
    return id;
  },
);

export const updateStock = createAsyncThunk(
  "products/updateStock",
  async ({ id, quantity, operation }) => {
    const token = localStorage.getItem("x-access-token");
    const response = await fetch(
      Configuration.BACK_BASEURL + `products/stock/${id}/${operation}`,
      {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          /* "x-access-token": token, */
        },
        body: JSON.stringify({ quantity }),
      },
    );
    const product = await response.json();
    return product;
  },
);

export const uploadProductImage = createAsyncThunk(
  "products/uploadImage",
  async (imageFile) => {
    const token = localStorage.getItem("x-access-token");
    const formData = new FormData();
    formData.append("image", imageFile);

    const response = await fetch(
      Configuration.BACK_BASEURL + `products/upload-image`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          /* "x-access-token": token, */
        },
        body: formData,
      },
    );
    const result = await response.json();
    return result;
  },
);

const productsSlice = createSlice({
  name: "products",
  initialState: {
    entities: [],
    loading: false,
    error: null,
    currentProduct: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get Product By Id
      .addCase(productGetById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(productGetById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(productGetById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Add Product
      .addCase(productAdded.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(productAdded.fulfilled, (state, action) => {
        state.loading = false;
        state.entities.push(action.payload);
        state.error = null;
      })
      .addCase(productAdded.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Update Product
      .addCase(productUpdated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(productUpdated.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entities.findIndex(
          (prod) => prod.id === action.payload.id,
        );
        if (index !== -1) {
          state.entities[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(productUpdated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Delete Product
      .addCase(productDeleted.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(productDeleted.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = state.entities.filter(
          (prod) => prod.id !== action.payload,
        );
        state.error = null;
      })
      .addCase(productDeleted.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update Stock
      .addCase(updateStock.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStock.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.entities.findIndex(
          (prod) => prod.id === action.payload.id,
        );
        if (index !== -1) {
          state.entities[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearError, clearCurrentProduct } = productsSlice.actions;
export default productsSlice.reducer;
